/** Dialog, Popover, Tooltip, DropdownMenu — Base UI overlays in house style. */
import { Dialog as BaseDialog } from "@base-ui-components/react/dialog";
import { Menu as BaseMenu } from "@base-ui-components/react/menu";
import { Popover as BasePopover } from "@base-ui-components/react/popover";
import { Tooltip as BaseTooltip } from "@base-ui-components/react/tooltip";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { useOutsidePressCapture } from "./use-outside-press";

/**
 * Base UI composes via a `render` prop (a ReactElement to clone merged props
 * onto) instead of Radix's `asChild` + `Slot`. This translates the house
 * `asChild` API onto that mechanism so call sites (`<XTrigger asChild>`)
 * don't change. Must NOT pass `children` alongside `render` — Base UI's merge
 * gives the rendered element's own `children` priority, but only overwrites
 * keys actually present on it, so a stray `children` key here could leak
 * through unmerged on children-less elements (e.g. `<input />`).
 */
function triggerProps(asChild: boolean | undefined, children: React.ReactNode) {
  return asChild ? { render: children as React.ReactElement } : { children };
}

// ---- Dialog -----------------------------------------------------------------

export const Dialog = BaseDialog.Root;

// Granular primitives, exported alongside the bundled `DialogContent` below so
// a caller with a bespoke popup shape (e.g. the command palette, which has no
// title bar / close button) can compose Base UI's portal + backdrop + focus
// trap directly instead of reinventing them.
export const DialogPortal = BaseDialog.Portal;
export const DialogPopup = BaseDialog.Popup;
export const DialogTitle = BaseDialog.Title;

export const DialogTrigger = React.forwardRef<
  HTMLElement,
  { asChild?: boolean; children: React.ReactNode } & Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "children"
  >
>(function DialogTrigger({ asChild, children, ...props }, ref) {
  return <BaseDialog.Trigger ref={ref} {...triggerProps(asChild, children)} {...props} />;
});

/** Shared backdrop treatment for every Base UI dialog popup. */
export function DialogBackdrop() {
  return (
    <BaseDialog.Backdrop className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px] data-[open]:animate-rise-in" />
  );
}

export function DialogContent({
  className,
  children,
  title,
}: {
  className?: string;
  children: React.ReactNode;
  title: string;
}) {
  return (
    <DialogPortal>
      <DialogBackdrop />
      {/* Horizontal centering uses inset-x-0 + mx-auto (margin, not transform):
          the rise-in animation animates `transform`, and with fill-mode `both`
          its final keyframe would permanently override a translate-x-based
          centering — leaving every dialog shifted right by half its width. */}
      <DialogPopup
        className={cn(
          "fixed inset-x-0 top-[18%] z-50 mx-auto w-[min(560px,92vw)] rounded-md border border-border bg-popover p-5 text-popover-foreground shadow-deep data-[open]:animate-rise-in",
          className,
        )}
      >
        <div className="mb-3 flex items-center justify-between">
          <DialogTitle className="font-display text-base font-medium text-foreground">{title}</DialogTitle>
          <BaseDialog.Close className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
            <X className="h-4 w-4" />
          </BaseDialog.Close>
        </div>
        {children}
      </DialogPopup>
    </DialogPortal>
  );
}

// ---- Popover -----------------------------------------------------------------

// The house Popover owns its own open state so PopoverContent can close it via
// the capture-phase outside-press hook (the reader's gesture layers eat the
// bubble-phase pointerdown that Radix's own dismissal relied on — see
// use-outside-press). Base UI's built-in dismissal happens to *also* listen on
// `document` in the capture phase (so it likely isn't starved either), but
// this hook is the tested, guaranteed mechanism and is cheap to keep running
// alongside it — both just call the same `setOpen(false)`. Base UI's own
// onOpenChange still drives trigger clicks and Escape.
type PopoverContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
};

const PopoverContext = React.createContext<PopoverContextValue | null>(null);

export function Popover({
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  modal,
}: {
  children: React.ReactNode;
  /** Controlled open state; omit for the uncontrolled (self-owned) behavior. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  return (
    <PopoverContext.Provider value={{ open, setOpen: handleOpenChange, triggerRef }}>
      <BasePopover.Root open={open} onOpenChange={handleOpenChange} modal={modal}>
        {children}
      </BasePopover.Root>
    </PopoverContext.Provider>
  );
}

export const PopoverTrigger = React.forwardRef<
  HTMLElement,
  { asChild?: boolean; children: React.ReactNode } & Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "children"
  >
>(function PopoverTrigger({ asChild, children, ...props }, forwardedRef) {
  const ctx = React.useContext(PopoverContext);
  // Capture the resolved trigger element so PopoverContent's outside-press
  // check can exclude it (a press on the trigger toggles via Base UI instead).
  const setRef = React.useCallback(
    (node: HTMLElement | null) => {
      if (ctx) ctx.triggerRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [ctx, forwardedRef],
  );
  return <BasePopover.Trigger ref={setRef} {...triggerProps(asChild, children)} {...props} />;
});

export function PopoverContent({
  className,
  children,
  align = "end",
  side,
}: {
  className?: string;
  children: React.ReactNode;
  align?: "start" | "center" | "end";
  /** Which side of the trigger to open on. Defaults to Base UI's "bottom";
   * a trigger docked to the bottom of the viewport wants "top". */
  side?: "top" | "bottom" | "left" | "right";
}) {
  const ctx = React.useContext(PopoverContext);
  const contentRef = React.useRef<HTMLDivElement>(null);

  useOutsidePressCapture(
    () => contentRef.current,
    () => ctx?.triggerRef.current ?? null,
    () => ctx?.setOpen(false),
    ctx?.open ?? false,
  );

  return (
    <BasePopover.Portal>
      <BasePopover.Positioner align={align} side={side} sideOffset={8}>
        <BasePopover.Popup
          ref={contentRef}
          className={cn(
            "z-50 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-deep data-[open]:animate-rise-in",
            className,
          )}
        >
          {children}
        </BasePopover.Popup>
      </BasePopover.Positioner>
    </BasePopover.Portal>
  );
}

// ---- Tooltip -----------------------------------------------------------------

export const TooltipProvider = BaseTooltip.Provider;

export function Tip({
  label,
  children,
  side = "bottom",
}: {
  label: React.ReactNode;
  children: React.ReactNode;
  side?: "top" | "bottom" | "left" | "right";
}) {
  return (
    <BaseTooltip.Root>
      <BaseTooltip.Trigger delay={350} render={children as React.ReactElement} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner side={side} sideOffset={6}>
          <BaseTooltip.Popup className="z-50 rounded border border-border bg-popover px-2 py-1 text-2xs text-foreground shadow-deep">
            {label}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}

// ---- Dropdown ----------------------------------------------------------------

export const Dropdown = BaseMenu.Root;

export const DropdownTrigger = React.forwardRef<
  HTMLElement,
  { asChild?: boolean; children: React.ReactNode } & Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    "children"
  >
>(function DropdownTrigger({ asChild, children, ...props }, ref) {
  return <BaseMenu.Trigger ref={ref} {...triggerProps(asChild, children)} {...props} />;
});

export function DropdownContent({
  children,
  align = "start",
}: {
  children: React.ReactNode;
  align?: "start" | "center" | "end";
}) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner align={align} sideOffset={6}>
        <BaseMenu.Popup className="z-50 min-w-[180px] rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-deep data-[open]:animate-rise-in">
          {children}
        </BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

export function DropdownItem({
  children,
  onSelect,
  disabled,
  closeOnClick,
}: {
  children: React.ReactNode;
  onSelect?: () => void;
  disabled?: boolean;
  /** Keep the menu open when the item is clicked (default: Base UI closes it).
   * Set `false` for an item whose action runs asynchronously and shows its own
   * in-item busy spinner — the menu is closed by the caller once it settles. */
  closeOnClick?: boolean;
}) {
  return (
    <BaseMenu.Item
      disabled={disabled}
      closeOnClick={closeOnClick}
      onClick={onSelect}
      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-[13px] text-foreground outline-none data-[disabled]:opacity-40 data-[highlighted]:bg-accent"
    >
      {children}
    </BaseMenu.Item>
  );
}
